import type {
  Melody,
  MelodyDuration,
  MelodyEntityNote,
  MelodyGrid,
  MelodyNote,
  MelodyTempo,
  NoteName,
} from '@tunasong/schemas'
import { standardNoteNames } from '@tunasong/schemas'
import { getNoteChromaticIndex } from '../note/note-lib.js'
import { snapToSlot } from './grid.js'

/**
 * Converters between the melody ENTITY note (MIDI number, beats, offset) and the AI
 * melody-suggestion note (`melodyNoteSchema`: note name, octave, enumerated duration, beat
 * position).
 *
 * The two shapes are not interchangeable and the entity is the store. Duration conversion depends
 * on the melody's GRID, because a triplet eighth is an EIGHTH under a bracket, not a sixteenth:
 *
 *   - **Triplet grids (`'8t'`, `'16t'`)** convert by RATIO. Going out, multiply the real length by
 *     3/2 and take the nearest enum: 1/3 beat -> `'8n'`, 1/6 -> `'16n'`, 2/3 -> `'4n'`. Coming in,
 *     multiply by 2/3. This is what a musician reads off the page.
 *   - **Straight grids** take the notated value at face value, and round out by linear distance in
 *     beats.
 *
 * Going entity -> suggestion is LOSSY, and none of it is recoverable:
 *
 *   1. **The triplet-ness lives in the grid, not in the note.** The suggestion shape has no
 *      triplet bracket, so a triplet melody exported and re-imported against a STRAIGHT grid comes
 *      back straightened - the eighths that were thirds of a beat become halves. This converter
 *      exists for the AI melody-suggestion path and is lossy by design; it is not a serialiser.
 *   2. Durations that are not an enum value are ROUNDED. On an exact tie the SHORTER value wins:
 *      a lengthened note overruns the next onset, while a shortened one never does.
 *   3. `offsetMs` is DROPPED. The suggestion shape has no field for it - which is exactly why the
 *      entity does not reuse it as the store (§2).
 *
 * `position` is NOT ratio-converted in either direction: it is a real beat position in the
 * melody, not a notated one, so it means the same thing on both sides.
 *
 * `velocity` has no suggestion counterpart either way: it is dropped going out, and defaults to
 * `SUGGESTION_VELOCITY` coming in.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §2
 */

/** Velocity given to a note that arrived without one. Also the default for a click-inserted note. */
export const SUGGESTION_VELOCITY = 0.8

/** How many quarter-note beats each enumerated suggestion duration is worth. */
export const DURATION_BEATS: Record<MelodyDuration, number> = {
  '1n': 4,
  '2n': 2,
  '4n': 1,
  '8n': 0.5,
  '16n': 0.25,
}

/**
 * SHORTEST first, so `reduce`'s strict `<` keeps the earlier entry on an exact tie and the tie
 * resolves SHORT. A lengthened note overruns the next onset; a shortened one never does.
 */
const DURATION_ENTRIES = (Object.entries(DURATION_BEATS) as [MelodyDuration, number][]).sort((a, b) => a[1] - b[1])

/** A note written as an eighth inside a triplet bracket sounds for two thirds of an eighth. */
const TRIPLET_RATIO = 3 / 2

const isTripletGrid = (grid: MelodyGrid): boolean => grid === '8t' || grid === '16t'

/**
 * Suggestion -> entity. Offsets are zero: a generated melody has no performance to preserve.
 *
 * On a triplet grid the notated duration is scaled DOWN by 2/3 - an `'8n'` on an `'8t'` grid is a
 * triplet eighth, a third of a beat. `position` is left alone: it is a real beat position.
 *
 * `tempo` is part of the signature because the onset grid and the offset bound are tempo-relative
 * and sub-project 2's quantizer feeds the same shape; it is not read while every offset is zero.
 */
export const melodyFromSuggestion = (notes: MelodyNote[], tempo: MelodyTempo, grid: MelodyGrid): MelodyEntityNote[] => {
  const scale = isTripletGrid(grid) ? 1 / TRIPLET_RATIO : 1
  return notes
    .map(note => ({
      midi: (note.octave + 1) * 12 + getNoteChromaticIndex(note.pitch),
      onset: snapToSlot(note.position, grid),
      duration: DURATION_BEATS[note.duration] * scale,
      velocity: SUGGESTION_VELOCITY,
      offsetMs: 0,
    }))
    .sort((a, b) => a.onset - b.onset)
}

/**
 * The enum value a real length is written as. On a triplet grid the length is first scaled UP by
 * 3/2 into the value a reader would see on the page; on a straight grid it is taken as written.
 * Nearest by linear distance in beats, ties short.
 */
const nearestDuration = (beats: number, grid: MelodyGrid): MelodyDuration => {
  const notated = isTripletGrid(grid) ? beats * TRIPLET_RATIO : beats
  return DURATION_ENTRIES.reduce((best, entry) =>
    Math.abs(entry[1] - notated) < Math.abs(best[1] - notated) ? entry : best
  )[0]
}

/** Entity -> suggestion. Lossy - see the module comment. */
export const melodyToSuggestion = (melody: Melody): MelodyNote[] =>
  melody.notes.map(note => ({
    pitch: standardNoteNames[note.midi % 12] as NoteName,
    octave: Math.floor(note.midi / 12) - 1,
    duration: nearestDuration(note.duration, melody.grid),
    position: note.onset,
  }))
